import {
  useIsdDistrictProgramEnhancementModal,
  useIsdDistrictProgramEnhancements
} from '../../../../../hooks/use-isdDistrictProgramEnhancements'
import { type IsdDistrictProgramEnhancement } from '../../../../../models/IsdDistrictProgramEnhancement'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import type { ThingsToConsiderSection } from '../ThingsToConsiderCard'
import { Box, CircularProgress, Typography } from '@mui/material'
import { BlueButton } from '../../../../customComponents/buttons/BlueButton'
import { AddCircle, PlaylistAddCheck } from '@mui/icons-material'
import { ThingsToConsiderButton } from '../ThingsToConsiderButton'
import { BasicTable } from '../BasicTable'
import { IsdDistrictProgramEnhancementModal, type UpdateIsdDistrictProgramEnhancementFormValues } from './IsdDistrictProgramEnhancementModal'
import { DeleteIsdDistrictProgramEnhancement, GetEEMIdByOrgId, UpdateIsdDistrictProgramEnhancement } from '../../../../../services/AnnualPlanService'
import { Modal } from '../../../../customComponents/Modal'
import { ProgramEnhancementTrackingModal } from './ProgramEnhancementTrackingModal'
import { SubBasicTable, type SubBasicTableColumn, type SubBasicTableRow } from '../SubBasicTable'
import { handleApiResponse } from '../../../../../utils/apiHelpers'

interface Props {
  priorityId: number
  planOrgId: number
  isdOrgId: number
  readOnly: boolean
  trackingReadonly: boolean
  showTableOnly?: boolean
  hideExpand?: boolean
  implementationPhase?: boolean
  budgetOverviewView?: boolean
}

export const IsdDistrictProgramEnhancementsList = (props: Props): JSX.Element => {
  const {
    isLoading,
    isdDistrictProgramEnhancements,
    reloadIsdDistrictProgramEnhancements
  } = useIsdDistrictProgramEnhancements(props.priorityId, props.implementationPhase)
  const [eemId, setEemId] = useState<number | null>(null)

  const {
    initialValues,
    handleCloseModal,
    showIsdDistrictProgramEnhancementModal,
    handleAddIsdDistrictProgramEnhancement,
    handleEditIsdDistrictProgramEnhancement,
    programEnhancementToTrack,
    handleTrackProgramEnhancement,
    handleCloseTrackingModal,
    programEnhancementTrackingToDelete,
    setProgramEnhancementTrackingToDelete
  } = useIsdDistrictProgramEnhancementModal()

  const [isdDistrictProgramEnhancementToDelete, setIsdDistrictProgramEnhancementToDelete] = useState<IsdDistrictProgramEnhancement | null>(null)

  const handleDeleteIsdDistrictProgramEnhancement = async (isdDistrictProgramEnhancement: IsdDistrictProgramEnhancement): Promise<void> => {
    const { success, errors } = await DeleteIsdDistrictProgramEnhancement(
      isdDistrictProgramEnhancement.id,
      isdDistrictProgramEnhancement.isUpdate,
      props.implementationPhase
    )

    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }

    await reloadIsdDistrictProgramEnhancements()
    toast.success('Enhancement Plan Deleted')
    setIsdDistrictProgramEnhancementToDelete(null)
  }

  useEffect(() => {
    // get eem id from isdOrgid
    const fetchData = async (): Promise<void> => {
      if (props.isdOrgId == null || props.isdOrgId <= 0) return
      const eemId: number | null = await handleApiResponse(GetEEMIdByOrgId(props.isdOrgId))
      setEemId(eemId)
    }

    void fetchData()
  }, [props.isdOrgId])

  const thingsToConsiderSections: ThingsToConsiderSection[] = [
    {
      title: 'Examples of Activities to Expand Peer to Peer Programs',
      items: [
        'Establish a Peer to Peer Leadership Committee at the ISD or district',
        'Support Peer to Peer during extracurricular activities',
        <>Establish a partnership between the Peer to Peer program and <a style={{ color: 'currentcolor' }} target='_blank' href='https://www.generationunified.org/'>Unified Champion Schools</a></>,
        'Collect survey data from all stakeholders',
        'Promote your Peer to Peer program in the local media to raise awareness',
        'Develop a Peer to Peer website',
        'Create and implement a fundraising plan (e.g., connect with local businesses, grants, school fundraisers)',
        'Train teachers to utilize LINKS Boxes and the Peer to Peer Elementary and Secondary Curriculum',
        'Develop specific plans to involve families in Peer to Peer programs',
        'Involve students in training others'
      ]
    }
  ]

  const isEmpty = isdDistrictProgramEnhancements.length === 0
  const columns = [
    { id: 'organizationInfo', label: 'ISD/District Info', minWidth: 200 },
    { id: 'actions', label: 'Action(s)', minWidth: 200 },
    { id: 'resources', label: 'Resources', minWidth: 200 },
    { id: 'budgetRequestString', label: 'Budget', minWidth: 100 }
  ]

  const expandedProgramEnhancementContent = (programEnhancement: IsdDistrictProgramEnhancement): JSX.Element | null => {
    const hasProgress = programEnhancement.currentProgress != null && programEnhancement.currentProgress.length > 0
    const hasTrackingDetails = hasProgress || programEnhancement.actualBudgetAmount != null
    if (!hasTrackingDetails) return null

    const columns: SubBasicTableColumn[] = [
      { id: 'currentProgressString', label: 'Current Progress', isTrackingColumn: true },
      { id: 'actualBudgetAmountString', label: 'Budget Used', isTrackingColumn: true },
      { id: 'trackingAction', label: '', isTrackingColumn: true }
    ]

    const actualBudgetAmountString: string = programEnhancement.actualBudgetAmount != null && !(isNaN(programEnhancement.actualBudgetAmount))
      ? `$${Number(programEnhancement.actualBudgetAmount).toLocaleString()}`
      : '-'

    const rows: SubBasicTableRow[] = [
      {
        ...programEnhancement,
        currentProgressString: programEnhancement.currentProgress?.join(', ') ?? '-',
        actualBudgetAmountString
      }
    ]

    const handleEditTracking = (programEnhancement: IsdDistrictProgramEnhancement): void => {
      void handleTrackProgramEnhancement(programEnhancement)
    }
    const handleDeleteTracking = (programEnhancement: IsdDistrictProgramEnhancement): void => {
      setProgramEnhancementTrackingToDelete(programEnhancement)
    }

    return (
      <Box
        sx={{
          p: '2rem',
          backgroundColor: 'var(--grey-100)'
        }}
      >
        <SubBasicTable
          dataTestId='programEnhancementTrackingTable'
          columns={columns}
          rows={rows}
          readonly={props.readOnly}
          trackingReadonly={props.trackingReadonly}
          hasTrackingDetails={hasTrackingDetails}
          onEditTracking={handleEditTracking}
          onDeleteTracking={handleDeleteTracking}
        />
      </Box>
    )
  }

  const rows = isdDistrictProgramEnhancements.map(pe => ({
    ...pe,
    isUpdated: pe.isUpdate,
    isTracked: pe.isUpdate && (pe.currentProgress != null && pe.currentProgress.length > 0),
    organizationInfo: <>
      <Typography variant='body1' fontWeight='700'>{pe.organization.name}</Typography>
      <Typography variant='body1'>{pe.organizationContactName}</Typography>
      <Typography variant='body1'>{pe.organizationContactEmail}</Typography>
    </>,
    budgetRequestString: '$' + pe.budgetRequest.toLocaleString(),
    expandedContent: expandedProgramEnhancementContent(pe)
  }))

  const handleDeleteProgramEnhancementTracking = async (programEnhancement: IsdDistrictProgramEnhancement): Promise<void> => {
    const updatedProgramEnhancement: UpdateIsdDistrictProgramEnhancementFormValues = {
      id: programEnhancement.id,
      priorityId: programEnhancement.priorityId,
      organizationType: programEnhancement.organizationType,
      organizationId: programEnhancement.organization.id,
      organizationContactName: programEnhancement.organizationContactName,
      organizationContactEmail: programEnhancement.organizationContactEmail,
      actions: programEnhancement.actions,
      resources: programEnhancement.resources,
      budgetRequest: programEnhancement.budgetRequest,
      isImplementationPhase: props.implementationPhase ?? false,
      isUpdate: programEnhancement.isUpdate,
      actualAmount: null,
      currentProgress: null
    }

    const { success, errors } = await UpdateIsdDistrictProgramEnhancement(updatedProgramEnhancement)
    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }

    await reloadIsdDistrictProgramEnhancements()
    toast.success('Program Enhancement Plan Tracking Deleted')
    setProgramEnhancementTrackingToDelete(null)
  }

  return <>
    {props.showTableOnly !== true && (
      <Box sx={{ width: '100%', alignItems: 'center' }}>
        {!props.readOnly &&
          <BlueButton onClick={handleAddIsdDistrictProgramEnhancement} dataTestId='addIsdDistrictProgramEnhancementButton'>
            Add Program Enhancement Plan <AddCircle sx={{ ml: '10px' }} fontSize='small' />
          </BlueButton>
      }

        <Box sx={{ float: 'right' }}>
          <ThingsToConsiderButton
            buttonDataTestId='isdDistrictProgramEnhancementsThingsToConsiderButton'
            thingsToConsiderSections={thingsToConsiderSections}
            cardDataTestId='isdDistrictProgramEnhancementsThingsToConsiderCard'
          />
        </Box>
      </Box>
    )}

    <Box
      data-testid='isdDistrictProgramEnhancementsList'
      sx={{
        border: '1px solid var(--light-blue-300)',
        backgroundColor: 'var(--white)',
        borderRadius: '8px',
        padding: isEmpty ? '24px' : '0',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px 10px 10px -8px #3B353E4D'
      }}
    >
      {isLoading
        ? <CircularProgress sx={{ alignSelf: 'center' }} />
        : (isEmpty
            ? <>
              <PlaylistAddCheck sx={{ color: 'var(--light-blue-300)', width: '144px', height: '144px', alignSelf: 'center' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                <Typography variant='h4'>No Program Enhancement Plan Added</Typography>
                {!props.readOnly &&
                  <Typography variant='body1' fontSize='16px'>
                    Use the Add Program Enhancement Plan button to add a Program Enhancement Plan to your Annual Plan.
                  </Typography>
                }
              </Box>
            </>
            : <BasicTable
                dataTestId='isdDistrictProgramEnhancementsTable'
                columns={columns}
                rows={rows}
                isLoading={isLoading}
                hideExpand={props.hideExpand}
                readonly={props.readOnly}
                trackingReadonly={props.trackingReadonly}
                onEdit={handleEditIsdDistrictProgramEnhancement}
                onDelete={setIsdDistrictProgramEnhancementToDelete}
                implementationPhase={props.implementationPhase}
                hideDelete={false}
                onUpdate={handleEditIsdDistrictProgramEnhancement}
                onTrack={handleTrackProgramEnhancement}
                budgetOverviewView={props.budgetOverviewView}
            />
          )
      }

      {showIsdDistrictProgramEnhancementModal &&
        <IsdDistrictProgramEnhancementModal
          open={showIsdDistrictProgramEnhancementModal}
          priorityId={props.priorityId}
          planOrgId={props.planOrgId}
          isdOrgId={props.isdOrgId}
          eemId={eemId}
          onClose={handleCloseModal}
          onSaved={reloadIsdDistrictProgramEnhancements}
          initialValues={initialValues}
          implementationPhase={props.implementationPhase}
        />
      }

      {programEnhancementToTrack != null && (
        <ProgramEnhancementTrackingModal
          open={true}
          onClose={handleCloseTrackingModal}
          onSaved={reloadIsdDistrictProgramEnhancements}
          programEnhancement={programEnhancementToTrack}
          implementationPhase={props.implementationPhase ?? false}
        />
      )}

      {programEnhancementTrackingToDelete != null && (
        <Modal
          open={true}
          title='Are you sure?'
          cancelButtonText='No, Cancel'
          confirmButtonText='Yes, Delete'
          data-testid='confirmDeleteProgramEnhancementTrackingModal'
          bodyContent={'Are you sure you want to delete this item?'}
          onClose={() => { setProgramEnhancementTrackingToDelete(null) }}
          onConfirm={() => { void handleDeleteProgramEnhancementTracking(programEnhancementTrackingToDelete) }}
          maxWidth='xs'
        />
      )}

      {isdDistrictProgramEnhancementToDelete != null &&
        <Modal
          open={isdDistrictProgramEnhancementToDelete != null}
          title='Are you sure?'
          cancelButtonText='No, Cancel'
          confirmButtonText='Yes, Delete'
          data-testid='confirmDeleteIsdDistrictProgramEnhancementModal'
          bodyContent={'Are you sure you want to delete this item?'}
          onClose={() => { setIsdDistrictProgramEnhancementToDelete(null) }}
          onConfirm={() => { void handleDeleteIsdDistrictProgramEnhancement(isdDistrictProgramEnhancementToDelete) }}
          maxWidth='xs'
        />
      }
    </Box>
  </>
}
